import { CSSObject } from '@mui/material/styles';

import { enableMobileWebviewUi } from '../../hooks/useGetFeatureFlags';

/** Multi color accent shared by the ACA and pre-update MWG card treatments. */
export const CLUB_ACCENT_GRADIENT =
  'linear-gradient(to right, #e31b23, #a11a5e, #5b2d8e, #1b3fa0, #0a2f9a)';

interface ClubCardStyleArgs {
  /** The 4px bottom accent, painted through `background-image`. */
  accentImage: string;
  /**
   * Accent for the previous `::after` treatment, which rendered through
   * `background` and so takes either a solid color or a gradient.
   */
  legacyAccent: string;
  /** Card border that ships alongside the new accent. */
  border?: string;
}

/**
 * Bottom accent styling for club themed cards.
 *
 * The accent used to be drawn with an `::after` pseudo element, which bled past
 * the card's rounded corners. It is now painted as a background image instead.
 * The previous treatment stays behind `VITE_ENABLE_MOBILE_WEBVIEW_UI` so the
 * change can be turned off per environment.
 */
export const clubCardStyleOverrides = ({
  accentImage,
  legacyAccent,
  border,
}: ClubCardStyleArgs): CSSObject => {
  const base: CSSObject = {
    boxShadow: 'none',
    borderRadius: '16px',
    overflow: 'hidden',
    paddingBottom: '4px',
  };

  if (!enableMobileWebviewUi) {
    return {
      ...base,
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: legacyAccent,
      },
    };
  }

  return {
    ...base,
    ...(border && { border }),
    backgroundImage: accentImage,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom',
    backgroundSize: '100% 4px',
  };
};

export default clubCardStyleOverrides;
